<template>
  <div class="consulta-getran-modal">
    <form @submit.prevent="pesquisarAluno" class="search-form">
      <div class="form-grid">
        <Select :options="autoescolasFormatted" :modelValue="selectedCFC"
          @update:modelValue="$event => selectedCFC = $event" legend="Autoescola" placeholder="Selecione uma autoescola"
          :disabled="loading" />

        <Input mask="###.###.###-##" legend="CPF" placeholder="000.000.000-00" :modelValue="searchCPF"
          @update:modelValue="$event => searchCPF = $event" class="input-full input-cpf" />

        <Button type="submit" text="Pesquisar" color="primary" :disabled="loading || !selectedCFC || !searchCPF"
          class="search-button">
          <Magnify :size="18" />
        </Button>
      </div>
    </form>

    <div v-if="loading" class="loading-container">
      <LoadingSpinner />
      <p>Consultando dados no GETRAN...</p>
    </div>

    <div v-else-if="aulas.length > 0 || hasSearched" class="results-section">
      <h4 class="results-title">Resultados da Consulta</h4>

      <div class="table-container">
        <Table :headers="['Data', 'Início', 'Fim', 'Status']">
          <template v-if="aulas.length > 0">
            <tr v-for="(aula, index) in aulas" :key="index">
              <td>{{ aula.dataAula }}</td>
              <td>{{ aula.horaInicio }}</td>
              <td>{{ aula.horaFim }}</td>
              <td>
                <Badge :text="aula.status" :color="getStatusColor(aula.status)" />
              </td>
            </tr>
          </template>

          <template v-else>
            <tr class="no-results">
              <td colspan="4">
                <div class="empty-message">
                  <FileDocumentAlertOutline :size="32" color="#9E9E9E" />
                  <span>Nenhuma aula encontrada para este CPF</span>
                </div>
              </td>
            </tr>
          </template>
        </Table>
      </div>
    </div>

    <div class="modal-actions">
      <Button text="Fechar" color="primary" @handle:click="close">
        <Close :size="18" />
      </Button>
    </div>
  </div>
</template>
<script>
import urlApi from '@/router/urlApiV2'
import utils from '@/services/utils.js'

// Componentes Core
import Button from 'core/Button'
import LoadingSpinner from 'core/LoadingSpinner'
import Select from 'core/Select'
import Badge from 'core/Badge'
import Input from 'core/Input'
import Table from 'core/Table'

// Ícones
import Magnify from 'icons/Magnify'
import Close from 'icons/Close'
import FileDocumentAlertOutline from 'icons/FileDocumentAlertOutline'

export default {
  name: 'ConsultaGetranModal',
  components: {
    Button,
    LoadingSpinner,
    Select,
    Badge,
    Input,
    Table,
    Magnify,
    Close,
    FileDocumentAlertOutline
  },
  props: {
    autoescolas: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      selectedCFC: '',
      searchCPF: '',
      aulas: [],
      loading: false,
      hasSearched: false
    }
  },
  computed: {
    autoescolasFormatted() {
      return this.autoescolas.map(autoescola => ({
        value: autoescola.value,
        text: autoescola.text
      }))
    }
  },
  methods: {
    async pesquisarAluno() {
      if (!this.selectedCFC || !this.searchCPF) {
        this.$toasted.show('Selecione uma autoescola e digite um CPF', {
          position: 'top-right',
          type: 'error'
        })
        return
      }

      this.loading = true
      this.hasSearched = false

      try {
        const cpfNumerico = this.searchCPF.replace(/\D/g, '')
        const response = await this.$http.get(`${urlApi}/getran/aluno/${this.selectedCFC}/${cpfNumerico}`)

        this.aulas = response.body.aulas || []
        this.hasSearched = true

        if (this.aulas.length === 0) {
          this.$toasted.show('Nenhuma aula encontrada para este CPF', {
            position: 'top-right',
            type: 'info'
          })
        } else {
          this.$toasted.show(`${this.aulas.length} aula(s) encontrada(s)`, {
            position: 'top-right',
            type: 'success'
          })
        }
      } catch (error) {
        console.error('Erro ao consultar GETRAN:', error)
        this.$toasted.show('Erro ao consultar dados no GETRAN', {
          position: 'top-right',
          type: 'error'
        })
        this.aulas = []
        this.hasSearched = true
      } finally {
        this.loading = false
      }
    },

    formatCPF(event) {
      let value = event.target.value.replace(/\D/g, '')
      value = value.replace(/(\d{3})(\d)/, '$1.$2')
      value = value.replace(/(\d{3})(\d)/, '$1.$2')
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      this.searchCPF = value
    },

    formatDate(dateString) {
      if (!dateString) return ''
      return utils.formatDate(dateString) || dateString
    },

    getStatusColor(status) {
      const statusMap = {
        'Aceito': 'success',
        'Em análise': 'warning',
        'Rejeitado': 'danger',
        'Pendente': 'info'
      }
      return statusMap[status] || 'secondary'
    },

    close() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.consulta-getran-modal {
  max-width: 800px;
  min-width: 600px;

  @media (max-width: 768px) {
    min-width: auto;
    max-width: 95vw;
  }
}

.modal-title {
  margin: 0 0 1.5rem 0;
  color: #202224;
  font-size: 1.5rem;
  font-weight: 600;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.search-form {
  margin-bottom: 2rem;

  .form-grid {
    display: grid;
    grid-template-columns: 2fr 1.5fr auto;
    gap: 1rem;
    align-items: end;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .search-button {
      @media (max-width: 768px) {
        justify-self: center;
        min-width: 150px;
      }
    }
  }
}

.cpf-input {
  .input-legend {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.375rem;
  }

  .form-input {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s;
    background-color: #fff;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
      background-color: #f9fafb;
      color: #6b7280;
      cursor: not-allowed;
    }

    &::placeholder {
      color: #9ca3af;
    }
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;

  p {
    margin-top: 1rem;
    color: #6b7280;
    font-size: 0.875rem;
  }
}

.results-section {
  margin-bottom: 2rem;

  .results-title {
    margin: 0 0 1rem 0;
    color: #202224;
    font-size: 1.125rem;
    font-weight: 600;
  }
}

.table-container {
  overflow-x: auto;
  max-height: 25rem;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #fff;

  th {
    background-color: #f8fafc;
    padding: 0.75rem 1rem;
    text-align: left;
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
    border-bottom: 1px solid #e5e7eb;

    &:first-child {
      border-top-left-radius: 0.5rem;
    }

    &:last-child {
      border-top-right-radius: 0.5rem;
    }
  }

  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #f3f4f6;
    font-size: 0.875rem;
    color: #374151;
  }

  tbody tr {
    transition: background-color 0.2s;

    &:hover:not(.no-results) {
      background-color: #f8fafc;
    }

    &:last-child td {
      border-bottom: none;
    }
  }

  .no-results td {
    padding: 2rem 1rem;
    text-align: center;
  }
}

.empty-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;

  span {
    font-size: 0.875rem;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

// Responsividade
@media (max-width: 576px) {
  .results-table {
    font-size: 0.75rem;

    th,
    td {
      padding: 0.5rem;
    }
  }
}
</style>
